import { IAssistanceCategoryObject } from './assistance'
import { IMembershipRiderObject, IProgramServiceMembership } from './membership'
import {
  IAutochargeConfigResponseDTO,
  IWalletTransactionResponseDTO,
  IWalletWithSources,
  IWalletTransactionHistoryResponseDTO,
  IRefundTransactionDTO
} from './payment'
import { IRiderActivityLocationObject } from './location'
import { IUserEmail, IUserPhone } from './user'
import { IAppliedPromotion } from './promotions'
import { ITripRequestObject } from './trip'
import { ILogEntryResponseDTO } from './log-entry'
import { bool } from 'aws-sdk/clients/signer'

export interface IRiderFilters {
  orgId?: string
  skip?: number
  take?: number
  isActive?: boolean
  isArchived?: boolean
  isFirstArchived?: boolean
  isSecondArchived?: boolean
  isThirdArchived?: boolean
  membershipServiceIds?: string[]
  search?: string
  sortBy?: string[]
}

export interface IRiderSearchObject {
  id: string
  isArchived: boolean
  isActive: boolean
  name: string
  firstName: string
  lastName: string
  phone?: string
  countryCode: string
  email: string
  birthDate: string
  serviceIds: string[]
  serviceMemberships: any[]
  username: string
  organizationId: string
  organizationName: string
  createdAt: Date
  //Rider Dark Mode
  darkmode?: boolean
}

export interface IRiderReponseObject extends Omit<IRiderSearchObject, 'email' | 'phone'> {
  email: {
    emailAddress: string
  }
  phones: Array<{
    countryCode: string
    phoneNumber: string
  }>
}
export interface IRider {
  id: string
  image?: string
  codeCountry: number
  birthDate: string
  firstName: string
  lastName: string
  memberships: any
  phone?: number
  rating?: number
}

export interface IRiderAssistance {
  id: string
  instruction: string
  type: string
  category?: IAssistanceCategoryObject
}

export interface IRiderLocation {
  id: string
  city: string
  country: string
  fullAddress: string
  googlePlaceId: string
  latitude: number
  longitude: number
  name: string
  note: string
  order: number
  placeName: string
  state: string
  street: string
  streetNumber: string
  timeZone: string
  unit: string
  zip: string
}

export interface IRiderListItemProps {
  rider: IRiderObject
}

export interface IRiderListProps {
  isFetching: boolean
  riders: IRiderObject[]
  onClick: () => void
  onClickWithError: () => void
}

export interface IRiderMobility {
  id: string
  category: string
  active: boolean
  name: string
}

export interface IRiderMobilityObject {
  id: string
  isPrimary: boolean
  mobility: IRiderMobility
}

export interface IRiderObject {
  id: string
  name: string
  firstName: string
  lastName: string
  memberships: string[]
  membershipServiceIds: string[]
  email: string
  birthDate?: string
  phone?: string
  username: string
  countryCode: string
  isActive: boolean
  isArchived: boolean
  additionalInfo: string
  notificationPreference: string
  locations: string[]
  locationActivity: IRiderActivityLocationObject[]
  wallet: IWalletWithSources
  walletAutocharge: IAutochargeConfigResponseDTO
  walletTransactions: IWalletTransactionResponseDTO[]
  walletTransactionHistory: IWalletTransactionHistoryResponseDTO[]
  mobilityOptions: IRiderMobilityObject[]
  assistances: string[]
  saved: boolean
  allowFreeAttendant: boolean
  photo?: string
  ownerOrganization: string
  note?: string
  externalId?: string
  //Rider Dark Mode
  darkmode?: boolean
  disableMobileBooking?: boolean
  appliedPromotions?: {
    [id: string]: IAppliedPromotion
  }
  internalNote?: string
  riderRecurringTrips?: { items: ITripRequestObject[]; count: number }
  destroyedRecurringTaskId?: string
  refundDetails?: IRefundTransactionDTO
  refundedAmount?: number
  riderChangeLog?: { items: ILogEntryResponseDTO[]; count: number }
  orcaSerialNumber?: string
  riderReferralCode?: string
  paymentMode?: string
  serviceMemberships?: IMembershipRiderObject[]
  force?: {
    phoneNumber: boolean
  }
  caregiver?: Caregiver | null
}

export interface Caregiver {
  id?: string
  firstName: string
  lastName: string
  emailAddress?: string
  countryCode?: string
  contactNumber?: string
  relationship?: ECaregiverRelationship
  createdAt?: Date
  updatedAt?: Date
}

export enum ECaregiverRelationship {
  FAMILY = 'Family',
  INFORMAL = 'Informal',
  PROFESSIONAL = 'Professional',
  VOLUNTEER = 'Volunteer'
}

//NOTE: This does not represent the response from goin-payment
//it represents the data built in a react component during the
//add payment method flow
export interface IRiderPaymentSourceObject {
  id: string
  cardHolderName: string
  expMonth: number
  expYear: number
  isPrimary: boolean
  lastFourNumbers: string
  stripeCustomerId: string
}

export interface ISimpleRider {
  id: string
  notificationPreference: NotificationPreferenceTypes
  birthDate: Date
  createdAt: Date
  email?: IUserEmail
  firstName: string
  isActive: boolean
  isDeleted: boolean
  lastName: string
  middleName?: string
  note?: string
  phones: IUserPhone[]
  photo?: string
  updatedAt?: Date
  username?: string
  assistances?: IRiderAssistance[]
  locations?: IRiderLocation[]
  mobilities?: IRiderMobilityObject[]
  wallet: IWalletWithSources
  phone?: string
  codeCountry: string
  //Rider Dark Mode
  darkmode?: boolean
  appliedPromotions?: {
    [id: string]: IAppliedPromotion
  }
  //Added Internal Note
  internalNote?: string
  orcaSerialNumber?: string
}

export interface IUserTopPanelRider {
  id: string
  notificationPreference: NotificationPreferenceTypes
  birthDate: Date
  createdAt: Date
  email?: IUserEmail
  firstName: string
  isActive: boolean
  isDeleted: boolean
  lastName: string
  middleName?: string
  note?: string
  phones: IUserPhone[]
  photo?: string
  updatedAt?: Date
  username?: string
  assistances?: IRiderAssistance[]
  locations?: IRiderLocation[]
  mobilities?: IRiderMobilityObject[]
  wallet: IWalletWithSources
  phone?: string
  codeCountry: string
  //Rider Dark Mode
  darkmode?: boolean
  appliedPromotions?: IAppliedPromotion[]
  //Added Internal Note
  internalNote?: string
  orcaSerialNumber?: string
}

export enum NotificationPreferenceTypes {
  textMessage = 'text',
  voice = 'voice',
  none = 'none'
}

export interface IRiderWithServiceNotification {
  id: string
  firstName: string
  lastName: string
  serviceMemberships: IProgramServiceMembership[]
  birthDate: string
  phones: IUserPhone[]
  username: string
  isActive: boolean
  isArchived: boolean
  additionalInfo: string
  notificationPreference: string
  locations: string[]
  mobilityOptions: string[]
  assistances: string[]
  saved: boolean
  allowFreeAttendant: boolean
  activeMobilityDevice: string
  photo?: string
  email?: IUserEmail
  wallet: IWalletWithSources
  walletAutocharge: IAutochargeConfigResponseDTO
}

export interface ISearchRider {
  query: string
  organizationId: string
  status?: string
}

export interface IRiderChangeLogDTO {
  riderId: string
  skip?: number
  take?: number
}



// WEBPACK FOOTER //
// ./src/types/rider.ts